/* ============================================================
   DATA LOWONGAN - app/karir/data.js

   Co-located di folder rute, bukan app/data/, karena hanya halaman
   karir yang membacanya. Pola yang sama dengan
   app/(halaman)/edukasi-kesehatan/data.js.

   ============================================================
   >>> MENUNGGU KONFIRMASI HRD <<<

   1. `kualifikasi` - baris "Wanita maksimal 28 tahun" disalin apa
      adanya dari poster. Kalau HRD mengubah kriterianya, ubah di
      sini DAN minta poster baru; kalau hanya teksnya yang diubah,
      poster dan teks di halaman akan saling bertentangan.

   2. `KONTAK_HRD.email` - masih alamat Gmail dari poster. Kalau HRD
      pindah ke alamat @rsjmc.id, cukup ganti satu nilai ini; semua
      tombol "Lamar" ikut berubah.

   3. `ditutup` - poster tidak menyebut batas waktu. Selama null,
      lowongan tampil terus. Isi dengan tanggal ISO, contoh
      "2026-10-31T23:59:59+07:00", dan lowongannya hilang sendiri
      setelah tanggal itu.

   4. `ALAMAT_RS` - KOSONG, dan sengaja. Saya tidak tahu alamat resmi
      rumah sakit dan tidak mau mengarangnya. Selama kosong, data
      terstruktur untuk Google (JobPosting) tidak dipasang, karena
      Google mewajibkan lokasi kerja. Halamannya sendiri tetap
      tampil normal.

   5. admin-rawat-inap & admin-rawat-jalan - poster null dan
      kualifikasi kosong, MENUNGGU HRD. Rutenya sudah hidup; begitu
      HRD kirim kriteria, isi array `kualifikasi`, lalu minta poster
      dan ganti `poster: null` jadi objek {src, lebar, tinggi, alt}
      seperti dua entri di atasnya. Selama poster null, gambar tidak
      dirender (ada guard di KarirContent.jsx). Catatan: kalau nanti
      ALAMAT_RS.jalan diisi sementara kualifikasi admin masih kosong,
      JobPosting-nya menghasilkan <ul></ul> kosong - bukan galat,
      tapi Google tidak menyukainya, jadi isi kualifikasi dulu.
   ============================================================ */

export const KONTAK_HRD = {
  email: "hrd.rsjmc@gmail.com",
};

/* Diisi untuk mengaktifkan tampilan di pencarian lowongan Google.
   Salin dari alamat resmi RS - jangan dari poster Klinik Kirana,
   itu alamat klinik, bukan rumah sakit. */
export const ALAMAT_RS = {
  jalan: "",
  kota: "Jakarta Selatan",
  provinsi: "DKI Jakarta",
  kodePos: "",
};

/* Tanggal lowongan pertama kali dipasang di situs. Dipakai Google
   untuk menandai lowongan baru. Sesuaikan dengan hari tayang. */
const TAYANG = "2026-09-22";

export const LOWONGAN = [
  {
    slug: "apoteker",
    posisi: { id: "Apoteker", en: "Pharmacist" },
    tayang: TAYANG,
    ditutup: null,
    poster: {
      src: "/images/karir/apoteker.png",
      lebar: 1254,
      tinggi: 1254,
      alt: {
        id: "Poster lowongan kerja Apoteker di RS Jakarta Medical Center.",
        en: "Job vacancy poster for a Pharmacist at Jakarta Medical Center Hospital.",
      },
    },
    kualifikasi: [
      { id: "Wanita maksimal 28 tahun", en: "Female, maximum age 28" },
      { id: "Pendidikan minimal Profesi Apoteker", en: "Minimum education: Pharmacist professional degree" },
      { id: "Memiliki STRA aktif", en: "Active pharmacist registration (STRA)" },
      {
        id: "Berpengalaman lebih diutamakan (dibuktikan dengan paklaring); fresh graduate dipersilakan melamar",
        en: "Experience preferred (with a reference letter); fresh graduates welcome",
      },
      { id: "Bersedia bekerja dalam sistem shift", en: "Willing to work shifts" },
      { id: "Jujur, disiplin, dan memiliki komunikasi yang baik", en: "Honest, disciplined, good communicator" },
    ],
    berkas: {
      id: "CV, paklaring, STRA, dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV, reference letter, STRA and other supporting documents. State your expected salary.",
    },
  },
  {
    slug: "asisten-apoteker",
    posisi: { id: "Asisten Apoteker", en: "Pharmacy Assistant" },
    tayang: TAYANG,
    ditutup: null,
    poster: {
      src: "/images/karir/asisten-apoteker.png",
      lebar: 1254,
      tinggi: 1254,
      alt: {
        id: "Poster lowongan kerja Asisten Apoteker di RS Jakarta Medical Center.",
        en: "Job vacancy poster for a Pharmacy Assistant at Jakarta Medical Center Hospital.",
      },
    },
    kualifikasi: [
      { id: "Wanita maksimal 28 tahun", en: "Female, maximum age 28" },
      { id: "Pendidikan minimal D3 Farmasi", en: "Minimum education: Diploma (D3) in Pharmacy" },
      { id: "Memiliki STR aktif", en: "Active registration certificate (STR)" },
      {
        id: "Berpengalaman lebih diutamakan (dibuktikan dengan paklaring); fresh graduate dipersilakan melamar",
        en: "Experience preferred (with a reference letter); fresh graduates welcome",
      },
      { id: "Bersedia bekerja dalam sistem shift", en: "Willing to work shifts" },
      { id: "Jujur, disiplin, dan memiliki komunikasi yang baik", en: "Honest, disciplined, good communicator" },
    ],
    berkas: {
      id: "CV, paklaring, STR, dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV, reference letter, STR and other supporting documents. State your expected salary.",
    },
  },

  /* ---- MENUNGGU HRD: poster & kualifikasi (lihat catatan 5) ---- */
  {
    slug: "admin-rawat-inap",
    posisi: { id: "Admin Rawat Inap", en: "Inpatient Admin" },
    tayang: TAYANG,
    ditutup: null,
    poster: null,
    kualifikasi: [],
    berkas: {
      id: "CV dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV and other supporting documents. State your expected salary.",
    },
  },
  {
    slug: "admin-rawat-jalan",
    posisi: { id: "Admin Rawat Jalan", en: "Outpatient Admin" },
    tayang: TAYANG,
    ditutup: null,
    poster: null,
    kualifikasi: [],
    berkas: {
      id: "CV dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV and other supporting documents. State your expected salary.",
    },
  },
  {
    slug: "perawat",
    posisi: { id: "Perawat", en: "Nurse" },
    tayang: TAYANG,
    ditutup: null,
    poster: null,
    kualifikasi: [],
    berkas: {
      id: "CV dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV and other supporting documents. State your expected salary.",
    },
  },
    {
    slug: "driver",
    posisi: { id: "Driver Ambulance", en: "Ambulance Driver" },
    tayang: TAYANG,
    ditutup: null,
    poster: {
      src: "/images/karir/driver-ambulance.png",
      lebar: 0, // <-- GANTI dengan lebar asli file poster (px)
      tinggi: 0, // <-- GANTI dengan tinggi asli file poster (px)
      alt: {
        id: "Poster lowongan kerja Driver Ambulance di RS Jakarta Medical Center.",
        en: "Job vacancy poster for an Ambulance Driver at Jakarta Medical Center Hospital.",
      },
    },
    kualifikasi: [
      { id: "Laki-laki usia maksimal 24 tahun", en: "Male, maximum age 24" },
      { id: "Pendidikan minimal SMA sederajat", en: "Minimum education: senior high school or equivalent" },
      { id: "Memiliki SIM A aktif", en: "Active driving license (SIM A)" },
      {
        id: "Memiliki pengalaman menggunakan ambulance lebih diutamakan",
        en: "Experience driving an ambulance preferred",
      },
      { id: "Jujur, disiplin, dan bertanggung jawab", en: "Honest, disciplined, and responsible" },
      {
        id: "Memiliki sertifikat Defensive Driving Training (DDT) menjadi nilai plus",
        en: "Defensive Driving Training (DDT) certificate is a plus",
      },
      {
        id: "Mampu berkomunikasi dengan baik, bekerja dalam tim, serta siap siaga dengan sistem shift atau on call",
        en: "Able to communicate well, work in a team, and ready for shift or on-call duty",
      },
    ],
    berkas: {
      id: "CV, paklaring, ijazah, dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV, reference letter, diploma and other supporting documents. State your expected salary.",
    },
  },
  {
    slug: "kasir",
    posisi: { id: "Kasir", en: "Cashier" },
    tayang: TAYANG,
    ditutup: null,
    poster: null,
    kualifikasi: [],
    berkas: {
      id: "CV dan kelengkapan berkas lainnya. Cantumkan gaji yang diinginkan.",
      en: "CV and other supporting documents. State your expected salary.",
    },
  },
];

/* Lowongan yang masih dibuka pada `sekarang`. Menerima waktu sebagai
   argumen, tidak memanggil new Date() sendiri - pemanggilnya yang
   menentukan kapan waktu dihitung, supaya peladen dan browser tidak
   merender daftar yang berbeda. */
export function masihDibuka(lowongan, sekarang) {
  if (!lowongan.ditutup) return true;
  return sekarang.getTime() <= new Date(lowongan.ditutup).getTime();
}

/* Tautan mailto dengan subjek terisi. Pelamar yang lupa menulis
   subjek adalah sebab paling umum lamaran tenggelam di kotak masuk
   HRD; subjek yang seragam juga memudahkan HRD menyaringnya. */
export function tautanLamar(lowongan) {
  const subjek = `Lamaran ${lowongan.posisi.id} - [Nama Lengkap]`;
  return `mailto:${KONTAK_HRD.email}?subject=${encodeURIComponent(subjek)}`;
}
